import React, { useState } from "react";
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Grid,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useWizard } from "./WizardContext";

/**
 * WizardContainer - Main wizard framework component
 * Provides step navigation, progress indication, and content rendering
 */
const WizardContainer = ({ 
  children, 
  currentStep = 1, 
  onStepChange, 
  totalSteps = 3,
  stepTitles = ["Select Blocks", "Customize Style", "Review & Export"],
  isStepValid = () => true,
  showProgress = true 
}) => {
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Handle step navigation with validation
  const handleNext = async () => {
    if (currentStep < totalSteps && isStepValid(currentStep)) {
      setIsTransitioning(true);
      await new Promise(resolve => setTimeout(resolve, 300)); // Smooth transition
      onStepChange(currentStep + 1);
      setIsTransitioning(false);
    }
  };

  const handleBack = async () => {
    if (currentStep > 0) {
      setIsTransitioning(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      onStepChange(currentStep - 1);
      setIsTransitioning(false);
    }
  };

  const handleStepClick = async (step) => {
    // Allow clicking on completed steps or current step
    if (step <= currentStep) {
      setIsTransitioning(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      onStepChange(step);
      setIsTransitioning(false);
    }
  };

  // Calculate progress percentage - start from step 1, not 0
  const progressPercent = currentStep === 0 ? 0 : ((currentStep - 1) / (totalSteps - 2)) * 100;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
      {/* Progress Bar */}
      {showProgress && (
        <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
          <LinearProgress 
            variant="determinate" 
            value={progressPercent}
            sx={{ height: 4, bgcolor: "#e0e0e0" }}
          />
        </Box>
      )}

      <Container maxWidth="xl" sx={{ 
        pt: currentStep > 0 ? { xs: 10, sm: 12 } : (showProgress ? 3 : 2), 
        pb: 4,
        px: { xs: 2, sm: 3 }
      }}>

        {/* Main Content Area */}
        <Box sx={{ position: "relative", minHeight: "85vh" }}>
          {isTransitioning && (
            <Box 
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "rgba(255,255,255,0.8)",
                zIndex: 100,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <LinearProgress sx={{ width: 200 }} />
            </Box>
          )}
          
          {/* Dynamic Content */}
          <Box 
            sx={{ 
              opacity: isTransitioning ? 0.3 : 1,
              transition: "opacity 0.3s ease"
            }}
          >
            {children}
          </Box>
        </Box>

        {/* Floating Navigation Controls - Only show if not on introduction */}
        {currentStep > 0 && <FloatingNavCard />}
      </Container>
    </Box>
  );
};

/**
 * Floating Navigation Card - Consistent across all wizard steps
 * Shows step progress, selected content, and navigation controls
 */
const FloatingNavCard = () => {
  const { 
    currentStep, 
    setCurrentStep, 
    selectedBlocks, 
    toggleBlock,
    isStepValid,
    resetWizard,
    stepTitles = ["Introduction", "Select Blocks", "Customize Style", "Review & Export"]
  } = useWizard();

  const totalSteps = stepTitles.length - 1; // Exclude introduction

  const handleNext = () => {
    if (currentStep < totalSteps && isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === totalSteps) {
      // Complete button clicked - reset wizard and go back to intro
      resetWizard();
      setCurrentStep(0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Paper 
      elevation={8}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        p: { xs: 1, sm: 2 },
        bgcolor: '#d6e8fa',
        border: '1px solid #d0e5fb',
        boxShadow: '0 2px 8px rgba(28, 67, 106, 0.1)',
        borderRadius: 0,
        borderBottomLeftRadius: { xs: 0, sm: 8 },
        borderBottomRightRadius: { xs: 0, sm: 8 },
        zIndex: 1000
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Left: Back Button */}
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ 
              textTransform: 'none', 
              minWidth: { xs: 'auto', sm: 120 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 1, sm: 2 }
            }}
          >
            <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {currentStep === 1 ? "Back to Intro" : "Back"}
            </Box>
            <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>Back</Box>
          </Button>
          
          {/* Center: Stepper */}
          <Box sx={{ 
            flex: 1, 
            mx: { xs: 1, sm: 2, md: 4 },
            display: { xs: 'none', sm: 'block' }
          }}>
            <Stepper 
              activeStep={currentStep - 1} 
              alternativeLabel
              sx={{
                "& .MuiStepLabel-label": { 
                  color: "#1C436A",
                  fontSize: "0.875rem",
                  fontWeight: "500"
                },
                "& .MuiStepLabel-label.Mui-active": { 
                  color: "#1C436A",
                  fontWeight: "bold"
                },
                "& .MuiStepLabel-label.Mui-completed": { 
                  color: "#CE950F"
                },
                "& .MuiStepIcon-root": {
                  color: "rgba(28, 67, 106, 0.3)",
                  "&.Mui-active": { color: "#1C436A" },
                  "&.Mui-completed": { color: "#CE950F" }
                }
              }}
            >
              {stepTitles.slice(1).map((title, index) => (
                <Step key={title}>
                  <StepLabel 
                    onClick={() => index + 1 <= currentStep ? setCurrentStep(index + 1) : null}
                    sx={{ 
                      cursor: index + 1 <= currentStep ? "pointer" : "default",
                      "&:hover": index + 1 <= currentStep ? { opacity: 0.8 } : {}
                    }}
                  >
                    {title}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          
          {/* Right: Status Text & Next Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              {currentStep === 1 && selectedBlocks.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Choose at least one block to get started
                </Typography>
              )}
              {currentStep === 2 && (
                <Typography variant="body2" color="primary" fontWeight="bold">
                  Customize your brand colors and fonts
                </Typography>
              )}
              {currentStep === 3 && (
                <Typography variant="body2" color="primary" fontWeight="bold">
                  Review and copy your website code
                </Typography>
              )}
            </Box>
            
            <Button
              variant="contained"
              endIcon={currentStep === totalSteps ? null : <ArrowForwardIcon />}
              onClick={handleNext}
              disabled={currentStep < totalSteps && !isStepValid(currentStep)}
              sx={{
                textTransform: 'none',
                bgcolor: currentStep === totalSteps ? "#CE950F" : undefined,
                '&:hover': {
                  bgcolor: currentStep === totalSteps ? '#B5850D' : undefined
                },
                minWidth: { xs: 'auto', sm: 120 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 2, sm: 3 }
              }}
            >
              {currentStep === totalSteps ? "Complete" : "Next"}
            </Button>
          </Box>
          
          {/* Mobile Step Indicator */}
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' }, 
            alignItems: 'center',
            gap: 1,
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1C436A' }}>
              Step {currentStep}/{totalSteps}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Paper>
  );
};

export default WizardContainer;